import { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './FlatCardPage.module.css';
import Header from '../../components/Header/Header';
import CardIconButton from '../../components/UI/CardIconButton/CardIconButton';
import { useFavorite } from '../../pages/FavoritePage/FavoriteContext';
import { useCompare } from '../../pages/ComparePage/CompareContext';
import { FlatCardData } from '../../components/CardsComponents/types';
import BuyCard from '../../components/BuyCard/BuyCard';
import Button from '../../components/UI/Button/Button';
import Footer from '../../components/Footer/Footer';
import StatusProject from '../../components/StatusProject/StatusProject';
import SimilarCard from '../../components/SimilarCard/SimilarCard';
import PreviewFlat from '../../components/FlatPageComponents/PreviewFlat/PreviewFlat';
import AdditionalInfo from '../../components/CardPageComponents/CardPageAdditionalInfo/CardPageAdditionalInfo';
import CardPageFeatures from '../../components/CardPageComponents/CardPageFeatures/CardPageFeatures';
import { HeaderType } from '../../components/Header/types/Header';
import { Helmet } from 'react-helmet';
import { useFlat } from './FlatContext';
import { PURCHASE_FLAT_PAGE } from '../../router/paths';
import Loader from '../../components/Loader/Loader';
import { LoaderContext } from '../../components/Loader/LoaderContext';
import ObjectNotFound from '../../components/ObjectNotFound/ObjectNotFound';
import { OBJECT_TYPES } from '../../components/ObjectNotFound/ObjectNotFound';
import { calculateDiscountedPrice, calculateDiscountedPricePerM2, formatPrice } from '../../utils/discountPriceUtils';
import { api } from '../../utils/api';
import { transformFlat, transformFlats, transformParkings } from '../../utils/transformers';
import cn from 'classnames';

function getDeclension(number: number, one: string, few: string, many: string): string {
  const mod10 = number % 10;
  const mod100 = number % 100;

  if (mod100 >= 11 && mod100 <= 14) {
    return many;
  }
  if (mod10 === 1) {
    return one;
  }
  if (mod10 >= 2 && mod10 <= 4) {
    return few;
  }
  return many;
}

export default function FlatCardPage() {
  const navigate = useNavigate();
  const { favorites, toggleFavorite } = useFavorite();
  const { compares, toggleCompare } = useCompare();
  const { setFlat: setFlatContext, selectedOption, setSelectedOption } = useFlat();

  const [flat, setFlatState] = useState<FlatCardData | null>(null);
  const [similarFlats, setSimilarFlats] = useState<FlatCardData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 1080);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const { id } = useParams();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { isInitialLoad, setIsInitialLoad } = useContext(LoaderContext);
  const [isLoading, setIsLoading] = useState(true);
  const [parkingCount, setParkingCount] = useState<number>(0);
  const [mortgagePeriod, setMortgagePeriod] = useState<number>(48);
  
  const updateWindowWidth = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', updateWindowWidth);
    return () => {
      window.removeEventListener('resize', updateWindowWidth);
    };
  }, []);

  const buttonStyle = {
    width: windowWidth <= 767 ? '100%' : 'fit-content',
  };

  useEffect(() => {
    const updateLayout = () => {
      const windowWidth = window.innerWidth;
      setIsMobile(windowWidth <= 1080);
    };

    updateLayout();

    window.addEventListener('resize', updateLayout);

    return () => {
      window.removeEventListener('resize', updateLayout);
    };
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [flatsResponse, selectedFlatResponse, parkingResponse, mortgageResponse] = await Promise.all([
          api.flats.getAll(),
          api.flats.getById(Number(id)),
          api.parking.getAll(),
          api.mortgage.getAll()
        ]);

        const flats = Array.isArray(flatsResponse) ? transformFlats(flatsResponse) : [];
        const selectedFlat = selectedFlatResponse ? transformFlat(selectedFlatResponse) : null;
        
        const parkingData = transformParkings(parkingResponse);
        const availableParkings = parkingData.filter(parking => parking.available === true);
        setParkingCount(availableParkings.length || 140);

        if (mortgageResponse && mortgageResponse.length > 0) {
          setMortgagePeriod(mortgageResponse[0].period);
        }
  
        if (!selectedFlat) {
          setError('Квартира не найдена');
        } else {
          setFlatState(selectedFlat);
          setFlatContext(selectedFlat);
          const filteredFlats = flats.filter((f, idx) => idx !== 0);
          setSimilarFlats(getRandomItems(filteredFlats, 3));
        }
      } catch (err) {
        setError('Произошла ошибка при загрузке данных');
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    };
  
    fetchData();
  }, [id, setFlatContext, setIsInitialLoad]);

  const getRandomItems = (array: FlatCardData[], count: number) => {
    const shuffled = array.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <ObjectNotFound type={OBJECT_TYPES.FLAT}/>;
  }

  if (!flat) {
    return <ObjectNotFound type={OBJECT_TYPES.FLAT}/>;
  }

  const isFavorite = favorites.some((item) => item.id === flat.id && item.type === flat.type);
  const isCompared = compares.some((item) => item.id === flat.id && item.type === flat.type);

  const handleToggleFavorite = () => {
    toggleFavorite(flat);
  };

  const handleToggleCompare = () => {
    toggleCompare(flat);
  };

  const parsePrice = (price: any): number => {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') return Number(price.replace(/\s/g, '')) || 0;
    return 0;
  };

  const pricePerM2 = parsePrice(flat.pricePerM2 ?? '0');

  const basePrice = parsePrice(flat.price ?? '0');
  const discountedPrice = calculateDiscountedPrice(
    flat.pricePerM2 ?? '0',
    flat.discountProperty ?? 0,
    flat.price ?? '0'
  );
  const computedPrice = parsePrice(discountedPrice);
  
  const fullPaymentAmount = computedPrice - (flat.discountFull || 0);
  
  const installmentPrice = basePrice;

  const isDiscounted = Boolean(flat.discountProperty && flat.discountProperty > 0);

  const installmentYears = Math.round(mortgagePeriod / 12);
  const downPayment = Math.round(installmentPrice * 0.3);
  const monthlyPayment = Math.round((installmentPrice - downPayment) / mortgagePeriod);
  const numberOfPayments = mortgagePeriod;

  const handleCardSelect = (option: 'full' | 'installment') => {
    setSelectedOption(option);
  };
  
  const displayedPrice = selectedOption === 'full' ? fullPaymentAmount : installmentPrice;

  const totalDiscountAmount = basePrice - fullPaymentAmount;

  const discountPercentage = flat.discountProperty ?? 0;

  const handleShareLink = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl)
  };

  const handlePrint = () => {
    const printStyle = document.createElement('style');
    printStyle.innerHTML = `
      @media print {
        @page {
          size: landscape;
          margin: 1cm;
        }

        body {
          height: 100vh !important;
          overflow: hidden !important;
        }

        body * {
          display: none !important;
        }

        .${styles.content},
        .${styles.info_buy_wrapper} {
          display: block !important;
          position: static !important;
          overflow: visible !important;
          height: auto !important;
          max-height: 100vh !important;
        }

        .previewFlat, 
        .${styles.buy_wrapper} {
          display: block !important;
          position: static !important;
          overflow: visible !important;
          height: auto !important;
        }

        * {
          background-image: none !important;
          box-shadow: none !important;
          border-radius: 0 !important;
        }

        h1 { font-size: 20px !important; }
        h2 { font-size: 16px !important; }
        h3 { font-size: 14px !important; }
        p  { font-size: 12px !important; }
        * {
          transition: none !important;
          animation: none !important;
        }
      }
    `;
    document.head.appendChild(printStyle);
    window.print();
    document.head.removeChild(printStyle);
  };

  const handleRouteClick = () => {
    window.open('https://yandex.ru/maps/11008/kaspiysk/?ll=47.622042%2C42.898893&mode=routes&rtext=~42.898949%2C47.621987&rtt=auto&ruri=~ymapsbm1%3A%2F%2Forg%3Foid%3D117585465488&z=18.53', '_blank');
  };

  const handleBuyClick = () => {
    if (flat) {
      setFlatContext(flat);
      navigate(PURCHASE_FLAT_PAGE.replace(':id', flat.id.toString()));
    }
  };

  const formattedPricePerM2 = formatPrice(flat.pricePerM2);


  const discountedPricePerM2 = flat.pricePerM2 && flat.discountProperty && flat.discountProperty > 0
    ? calculateDiscountedPricePerM2(flat.pricePerM2, flat.discountProperty)
    : formattedPricePerM2;

  const formatArea = (area: number) => {
    if (typeof area !== 'number' || isNaN(area)) return '';
    return area.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  };

  const formattedArea = formatArea(Number(flat.area));

  const BuyWrapper = () => {
    return (
      <div className={styles.buy_wrapper}>
        <div className={styles.icon_buttons}>
          <CardIconButton iconType='icon2' isActive={isFavorite} onClick={handleToggleFavorite} />
          <CardIconButton iconType='icon3' isActive={isCompared} onClick={handleToggleCompare} />
          <div className={styles.share_button_wrapper}>
            <CardIconButton iconType='share_icon' />
            <div className={styles.share_options}>
              <CardIconButton iconType='shareLinkIcon' onClick={handleShareLink} />
              <CardIconButton iconType='sharePrintIcon' onClick={handlePrint} />
            </div>
          </div>
        </div>
        <div className={styles.buy_info}>
          <div className={styles.title}>
            <h3>
              {flat.roomType} от {formattedArea} м²
            </h3>
            <h3>на {flat.floor} этаже</h3>
          </div>
          <div className={styles.price}>
            <h3>от {displayedPrice.toLocaleString('ru-RU')} ₽</h3>
            {isDiscounted && selectedOption === 'full' && (
              <div className={styles.price_no_discount}>
                <p className='text_body_b2'> от {basePrice.toLocaleString('ru-RU')} ₽</p>
              </div>
            )}
          </div>
          <div className={styles.price_per_m}>
            <span className='text_body_b2'>
              {selectedOption === 'full'
                ? `от ${discountedPricePerM2} ₽ за м²`
                : `от ${pricePerM2.toLocaleString('ru-RU')} ₽ за м²`}
            </span>
          </div>
        </div>
        <div className={styles.buy_cards_wrapper}>
        <BuyCard
          fullPaymentAmount={fullPaymentAmount}
          discountPercentage={discountPercentage}
          discountAmount={totalDiscountAmount}
          installmentYears={installmentYears}
          downPayment={downPayment}
          monthlyPayment={monthlyPayment}
          numberOfPayments={numberOfPayments}
          discountFull={flat.discountFull ?? 0}
          installmentPrice={installmentPrice}
          onCardSelect={handleCardSelect}
          selectedOption={selectedOption}
        />
        </div>
        <Button colorType='blue' roundType='small_rounded' onClick={handleBuyClick}>
          Забронировать
        </Button>
        <div className={cn('text_captions_c1', styles.underbutton_span)}>
          <span>
            Стоимость носит информационный характер и&nbsp;не&nbsp;является публичной офертой.
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>
          {`${flat?.roomType ? String(flat.roomType) : ''} от ${formattedArea} м² | Восток Строй`}
        </title>
        {/* <meta name="description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="keywords" content="недвижимость, девелопер, Каспийск, новостройки, квартиры" /> */}
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="noindex, nofollow" />
        {/* <link rel="canonical" href="https://vostokstroy.com/parking" /> */}

        {/* <meta property="og:title" content="Ведущему девелоперу Каспийска — 10 лет" />
        <meta property="og:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." /> */}
        {/* <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" /> */}
        {/* <meta property="og:url" content="https://vostokstroy.com/parking" /> */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Восток Строй" />

        <meta name="twitter:card" content="summary_large_image" />
        {/* <meta name="twitter:title" content="Ведущему девелоперу Каспийска — 10 лет" />
        <meta name="twitter:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." /> */}
        {/* <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" /> */}

        <link rel="icon" type="image/svg+xml" href="/favicons/status-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/status-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/status-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/status-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/status-fav-180x180.png" />
      </Helmet>
      {isInitialLoad  ? (
        <Loader />
      ) : (
        <>
      {!isTourOpen && <Header theme={HeaderType.Light} />}
      <div className={styles.content}>
        <div className={styles.info_buy_wrapper}>
          {flat && (
            <PreviewFlat setIsTourOpen={setIsTourOpen} flatData={flat} />
          )}
          {isMobile && <BuyWrapper />}
          <div className={styles.info_wrapper}>
            <div className={styles.features}>
              {(flat.specialOptions.length > 0 || flat.windowOptions.length > 0) && (
                <CardPageFeatures data={flat} type='flat' />
              )}
            </div>
            <div className={styles.similar_wrapper}>
              <div className={styles.block_title}>
                <h3>Похожие</h3>
              </div>
              <div className={styles.similar_content}>
                {similarFlats.map((similarFlat) => (
                  <SimilarCard
                    key={similarFlat.id}
                    data={similarFlat}
                    isDiscounted={
                      typeof similarFlat.discountProperty === 'number'
                        ? similarFlat.discountProperty > 0
                        : Boolean(similarFlat.discountProperty)
                    }
                  />
                ))}
              </div>
            </div>
            <div className={styles.parking_wrapper}>
              <h3 className={styles.block_title}>Не только квартиры</h3>
              <div
                className={styles.parking_content}
              >
                <div className={styles.content_overlay}>
                  <h3 className={styles.parking_subtitle}>В «Статусе» {parkingCount} {getDeclension(parkingCount, 'парковочное место', 'парковочных места', 'парковочных мест')}</h3>
                  <Button roundType='small_rounded' onClick={() => navigate('/parking/status')} width='fit-content'>
                    Машино-места
                  </Button>
                </div>
              </div>
            </div>
            <div className={styles.additional_info_wrapper}>
              <AdditionalInfo property={flat} />
            </div>
          </div>
        </div>
        {!isMobile && <BuyWrapper />}
      </div>
      <div className={styles.black_block}>
        <div className={styles.contact_info_container}>
          <div className={styles.info_card}>
            <div className={styles.card_content}>
              <h3 className={styles.address_text}>Наш офис находится на&nbsp;проспекте Омарова, 8</h3>
            </div>
            <Button roundType='small_rounded' onClick={handleRouteClick} style={buttonStyle}>
              Построить маршрут
            </Button>
          </div>

          <div className={styles.info_card}>
            <div className={styles.card_content}>
              <div className={styles.contact_details}>
                <h3 className={styles.phone_text}>+7 938 151-22-22</h3>
                <p className={cn('text-2_font', styles.hours_text)}>
                  Мы&nbsp;рады звонкам. Отвечаем с&nbsp;9:00 до&nbsp;19:00 по&nbsp;будням и&nbsp;с&nbsp;9:00 до&nbsp;18:00 по&nbsp;выходным.
                </p>
              </div>
            </div>
          </div>
        </div>
        <StatusProject />
      </div>
      <div className={styles.footer_wrapper}>
        <Footer theme='dark' />
      </div>
      </>
      )}
    </>
  );
}
